import { TmsContext, TmsContextType } from '@awaze/mfe-common.contexts.tms'
import {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { AGE_ACQUISITION_CLICKED_ON_POD_WITH_CTA } from 'src/constants'
import { trackOptimizelyClientsideEvent } from 'src/lib/optimizely/optimizely-for-client'
import { GlobalProps } from '../globalProps'

interface DataLayerProps {
  event_name: string
}

export function useObserverDataLayer<T>(
  datalayer: T,
  clickOptions?: {
    clickEventName: string
    clickSelector: string
  },
): {
  elementRef: MutableRefObject<HTMLDivElement | null>
  tms: TmsContextType
} {
  const tms = useContext(TmsContext)
  const { brand, contentfulLocale, optimizelySdkKey, host, awazeClient } =
    useContext(GlobalProps)
  const [isViewed, setIsViewed] = useState(false)
  const elementRef = useRef<HTMLDivElement | null>(null)
  function handleClick(e: Event) {
    const clickedTile = (e.target as HTMLElement)
      ?.closest(clickOptions?.clickSelector)
      ?.getAttribute('data-ga')

    if (clickedTile) {
      trackOptimizelyClientsideEvent(
        optimizelySdkKey,
        AGE_ACQUISITION_CLICKED_ON_POD_WITH_CTA,
        brand,
        contentfulLocale,
        host,
        awazeClient,
      )

      const data = datalayer[Number(clickedTile.match(/\d+$/)?.[0])]
      tms?.updateDataLayer('link', 'offer-click', {
        ...data,
        event_name: clickOptions?.clickEventName,
      })
    }
  }

  useEffect(() => {
    if (clickOptions) {
      const currentElement = elementRef.current

      if (currentElement) {
        currentElement.addEventListener('click', handleClick)

        // Cleanup: remove the event listener when the component unmounts
        return () => {
          currentElement.removeEventListener('click', handleClick)
        }
      }
    }
  }, [clickOptions])

  useEffect(() => {
    // Check if the element is in the viewport and if not create it
    window.tmsevents = window.tmsevents || []
    // Create an intersection observer
    const observer = new IntersectionObserver((entries) => {
      // entries[0] is the first (and only) entry in the array
      // isIntersecting is true if the element is in the viewport
      // Kept easy to ready

      if (entries?.[0].isIntersecting && datalayer && !isViewed) {
        if (Array.isArray(datalayer)) {
          datalayer.forEach((data) => {
            tms?.updateDataLayer('link', data?.event_name, data)
          })
        } else {
          tms?.updateDataLayer(
            'link',
            (datalayer as DataLayerProps)?.event_name,
            datalayer,
          )
        }

        setIsViewed(true)
      }
    })

    // Start observing the carousel container
    if (!isViewed) {
      observer.observe(elementRef.current)
    } else {
      observer.disconnect()
    }

    // Clean up by disconnecting the observer when the component unmounts
    return () => !isViewed && observer.disconnect()
  }, [isViewed])

  return { elementRef, tms }
}
